import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchClinicResources } from "../../../store/thunks/resourceThunk";
import { fetchClinics } from "../../../store/thunks/clinicThunk";
import { fetchResourceSchedules } from "../../../store/thunks/resourceScheduleThunk";
import {
  format,
  addDays,
  subDays,
  addWeeks,
  subWeeks,
  parseISO,
} from "date-fns";
import BookResourceModal from "../../../modals/BookResourceModal";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/24/solid";

const ResourceScheduleModal = ({ setModalOpen }) => {
  const dispatch = useDispatch();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedResourceId, setSelectedResourceId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showBookModal, setShowBookModal] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showWorkingHours, setShowWorkingHours] = useState(true);

  const resources = useSelector((state) => state.resources?.resources || []);
  const clinic = useSelector((state) => state.clinics?.selectedClinic || null);
  const resourceEvents = useSelector(
    (state) => state.resourceSchedules?.schedules || []
  );
  const eventsLoading = useSelector(
    (state) => state.resourceSchedules?.loading || false
  );

  useEffect(() => {
    dispatch(fetchClinics());
  }, [dispatch]);

  useEffect(() => {
    const loadResources = async () => {
      setIsLoading(true);
      if (clinic && clinic._id) {
        await dispatch(fetchClinicResources(clinic._id));
      }
      setIsLoading(false);
    };

    loadResources();
  }, [dispatch, clinic]);

  // Fetch resource schedules whenever currentDate changes
  useEffect(() => {
    const fetchEvents = async () => {
      if (!clinic || !clinic._id) return;
      const startDate = new Date(currentDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(currentDate);
      endDate.setHours(23, 59, 59, 999);
      try {
        await dispatch(
          fetchResourceSchedules({
            clinicId: clinic._id,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          })
        );
      } catch (error) {
        console.error("Failed to fetch resource schedules:", error);
      }
    };

    fetchEvents();
  }, [dispatch, clinic, currentDate]);

  const filteredResources = resources.filter((resource) => {
    const matchesSearch =
      searchTerm === "" ||
      resource.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSelected =
      selectedResourceId === "" || resource._id === selectedResourceId;
    return matchesSearch && matchesSelected;
  });

  const handleNextDay = () => {
    setCurrentDate((prevDate) => addDays(prevDate, 1));
  };

  const handlePreviousDay = () => {
    setCurrentDate((prevDate) => subDays(prevDate, 1));
  };

  const handleNextWeek = () => {
    setCurrentDate((prevDate) => addWeeks(prevDate, 1));
  };

  const handlePreviousWeek = () => {
    setCurrentDate((prevDate) => subWeeks(prevDate, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleTimeSlotClick = (resource, time) => {
    console.log(`Time slot clicked: ${time} for resource: ${resource.name}`);
    setSelectedTimeSlot(time);
    setSelectedResource(resource);
    setShowBookModal(true);
  };

  const handleContextMenu = (e, resource, time) => {
    e.preventDefault();
    console.log(
      `Right-clicked at time: ${time} for resource: ${resource.name}`
    );
    const contextMenu = document.getElementById("resource-context-menu");
    if (contextMenu) {
      contextMenu.style.display = "block";
      contextMenu.style.left = `${e.pageX}px`;
      contextMenu.style.top = `${e.pageY}px`;
      setSelectedTimeSlot(time);
      setSelectedResource(resource);
    }
  };

  useEffect(() => {
    const handleClickOutside = () => {
      const contextMenu = document.getElementById("resource-context-menu");
      if (contextMenu) {
        contextMenu.style.display = "none";
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleBookResource = () => {
    console.log(
      `Booking resource ${selectedResource?.name} at time ${selectedTimeSlot}`
    );
    setShowBookModal(true);
  };

  const handleEventClick = (event) => {
    console.log("Event clicked:", event);
    
    // If this event is associated with a meeting, we could do something special here
    // For now, just set it as the selected event for editing
    setSelectedEvent(event);
    setShowBookModal(true);
  };

  const getResourceEvents = (resourceId) => {
    if (!Array.isArray(resourceEvents)) {
      console.warn("resourceEvents is not an array:", resourceEvents);
      return [];
    }
    return resourceEvents.filter((event) => {
      if (event.resource && typeof event.resource === "object") {
        return event.resource._id === resourceId;
      }
      return event.resource === resourceId;
    });
  };

  const isDataLoading = isLoading || eventsLoading;

  // Define timeline parameters
  const slotHeight = 32; // each 15-min slot height in px
  const totalSlots = showWorkingHours ? 48 : 96; // 12 hours * 4 slots or 24 hours * 4 slots
  const startHour = showWorkingHours ? 8 : 0; // Start at 8 AM for working hours
  const timelineHeight = slotHeight * (showWorkingHours ? 48 : 96); // Adjust height based on view

  const getTimeForIndex = (index) => {
    const totalMinutes = index * 15 + startHour * 60;
    const hour = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;
    return {
      hour: hour.toString().padStart(2, "0"),
      minute: minute.toString().padStart(2, "0"),
    };
  };

  const getSlotIndexFromTime = (time) => {
    const hours = time.getHours();
    const minutes = time.getMinutes();
    return (hours - startHour) * 4 + Math.floor(minutes / 15);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center p-4 border-b">
        <h2 className="text-xl font-semibold">Resource Schedule</h2>
        <button
          onClick={() => setModalOpen(null)}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="p-4 border-b flex flex-wrap items-center gap-4 bg-white shadow-sm">
        <div className="flex items-center">
          <button
            onClick={handlePreviousWeek}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors">
            <ChevronDoubleLeftIcon className="h-5 w-5" />
          </button>

          <button
            onClick={handlePreviousDay}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors">
            <ChevronLeftIcon className="h-5 w-5" />
          </button>

          <button
            onClick={handleToday}
            className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-sm hover:bg-blue-200 transition-colors font-medium">
            Today
          </button>

          <button
            onClick={handleNextDay}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors">
            <ChevronRightIcon className="h-5 w-5" />
          </button>

          <button
            onClick={handleNextWeek}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors">
            <ChevronDoubleRightIcon className="h-5 w-5" />
          </button>

          <span className="text-lg font-medium">
            {format(currentDate, "MMMM d, yyyy")}
          </span>
        </div>

        <div className="flex-grow"></div>

        <div className="flex items-center gap-4">
          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={showWorkingHours}
              onChange={(e) => setShowWorkingHours(e.target.checked)}
              className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span>8AM - 8PM Only</span>
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
            />
            <div className="absolute left-3 top-2.5 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
          <select
            value={selectedResourceId}
            onChange={(e) => setSelectedResourceId(e.target.value)}
            className="border rounded-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm">
            <option value="">All Resources</option>
            {resources.map((resource) => (
              <option key={resource._id} value={resource._id}>
                {resource.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-grow overflow-hidden">
        <div className="flex-shrink-0 w-16 border-r border-gray-200 bg-white z-10">
          <div className="h-12 bg-gray-50" />
          <div
            className="overflow-y-scroll"
            style={{ height: "calc(100% - 48px)" }}
            id="time-markers-container">
            {Array.from({ length: totalSlots }).map((_, index) => {
              const { hour, minute } = getTimeForIndex(index);
              return (
                <div
                  key={`time-${index}`}
                  className={`h-8 flex items-center justify-end pr-2 text-xs text-gray-500 ${
                    index % 4 === 0 ? "font-medium" : ""
                  }`}>
                  {index % 4 === 0 && `${hour}:${minute}`}
                </div>
              );
            })}
          </div>
        </div>

        <div
          className="flex-grow overflow-x-auto overflow-y-scroll"
          id="calendar-scroll-container"
          onScroll={(e) => {
            const timeMarkers = document.getElementById(
              "time-markers-container"
            );
            if (timeMarkers) {
              timeMarkers.scrollTop = e.target.scrollTop;
            }
          }}>
          {isDataLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              No resources found
            </div>
          ) : (
            <div className="flex relative">
              {filteredResources.map((resource) => (
                <div
                  key={resource._id}
                  className="flex-shrink-0 w-60 border-r border-gray-200 relative">
                  <div className="h-12 p-2 border-b bg-gray-50 font-medium text-sm truncate sticky top-0 z-10">
                    {resource.name}
                  </div>
                  <div
                    className="relative"
                    style={{ height: timelineHeight }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      const rect = e.currentTarget.getBoundingClientRect();
                      const offsetY = e.clientY - rect.top;
                      const slotIndex = Math.floor(offsetY / slotHeight);
                      const hour = Math.floor(slotIndex / 4);
                      const minute = (slotIndex % 4) * 15;
                      const selectedTime = new Date(currentDate);
                      selectedTime.setHours(hour, minute, 0, 0);
                      const timeString = format(selectedTime, "HH:mm");
                      setSelectedTimeSlot(timeString);
                      setSelectedResource(resource);
                      setShowBookModal(true);
                    }}
                    onMouseMove={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const offsetY = e.clientY - rect.top;

                      // Update hover line for this column
                      const columnIndicator = e.currentTarget.querySelector(
                        ".column-hover-indicator"
                      );
                      const columnTooltip = e.currentTarget.querySelector(
                        ".column-time-tooltip"
                      );

                      if (columnIndicator && columnTooltip) {
                        // Calculate exact time including the start hour offset
                        const exactSlotIndex = offsetY / slotHeight;
                        const totalMinutes =
                          exactSlotIndex * 15 + startHour * 60;
                        const hour = Math.floor(totalMinutes / 60);
                        const minute = Math.floor(totalMinutes % 60);

                        const timeString = `${hour
                          .toString()
                          .padStart(2, "0")}:${minute
                          .toString()
                          .padStart(2, "0")}`;

                        // Position line exactly at mouse position
                        columnIndicator.style.transform = `translateY(${offsetY}px)`;
                        columnIndicator.style.opacity = "1";

                        // Update and position tooltip
                        columnTooltip.textContent = timeString;
                        columnTooltip.style.transform = `translateY(${offsetY}px)`;
                        columnTooltip.style.opacity = "1";
                      }
                    }}
                    onMouseLeave={(e) => {
                      const columnIndicator = e.currentTarget.querySelector(
                        ".column-hover-indicator"
                      );
                      const columnTooltip = e.currentTarget.querySelector(
                        ".column-time-tooltip"
                      );

                      if (columnIndicator && columnTooltip) {
                        columnIndicator.style.opacity = "0";
                        columnTooltip.style.opacity = "0";
                      }
                    }}>
                    {/* Hover indicator for this column */}
                    <div
                      className="column-hover-indicator absolute left-0 right-0 h-0.5 bg-red-400 pointer-events-none transition-transform duration-50"
                      style={{ opacity: 0, zIndex: 20 }}
                    />
                    <div
                      className="column-time-tooltip absolute right-1 bg-black text-white text-xs py-1 px-2 rounded pointer-events-none transition-all duration-50"
                      style={{ opacity: 0, zIndex: 20 }}>
                      12:00
                    </div>

                    {/* Grid lines */}
                    {Array.from({ length: totalSlots }).map((_, index) => (
                      <div
                        key={`grid-${index}`}
                        className="absolute left-0 right-0 h-8 border-b border-gray-100"
                        style={{ top: `${index * slotHeight}px` }}
                      />
                    ))}

                    {/* Events */}
                    {getResourceEvents(resource._id).map((event) => {
                      const startTime = parseISO(event.start);
                      const endTime = parseISO(event.end);

                      // Adjust indices for working hours view
                      const startIndex = getSlotIndexFromTime(startTime);
                      const endIndex = getSlotIndexFromTime(endTime);

                      // Only show events within the visible time range
                      if (startIndex < 0 || endIndex < 0) return null;

                      const top = startIndex * slotHeight;
                      const height = (endIndex - startIndex) * slotHeight;

                      return (
                        <div
                          key={event._id}
                          className={`absolute left-1 right-1 rounded p-1 text-xs cursor-pointer ${
                            event.meeting ? "bg-indigo-200 border border-indigo-300" : "bg-blue-200"
                          }`}
                          style={{ top: `${top}px`, height: `${height}px` }}
                          onClick={() => handleEventClick(event)}>
                          <div className="font-semibold truncate">
                            {event.meeting ? `Meeting: ${event.title}` : event.title || "Booked"}
                          </div>{" "}
                          {event.description && (
                            <div className="font-normal truncate">
                              {event.description}
                            </div>
                          )}
                          {event.clinicians && event.clinicians.length > 0 && (
                            <div className="truncate">
                              {event.clinicians.map((clinician) => (
                                <div key={clinician._id}>
                                  {clinician.firstName} {clinician.lastName}
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="truncate">
                            {format(startTime, "h:mm aa")} -{" "}
                            {format(endTime, "h:mm aa")}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        id="resource-context-menu"
        className="fixed hidden shadow-lg bg-white border rounded-sm py-1 z-50"
        style={{ minWidth: "180px" }}>
        <button
          onClick={handleBookResource}
          className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm">
          Book Resource
        </button>
      </div>

      {showBookModal && (
        <BookResourceModal
          onClose={() => {
            setShowBookModal(false);
            setSelectedEvent(null);
          }}
          resource={selectedEvent?.resource || selectedResource}
          selectedTime={
            selectedTimeSlot ||
            (selectedEvent
              ? format(parseISO(selectedEvent.start), "HH:mm")
              : null)
          }
          currentDate={currentDate}
          existingSchedule={selectedEvent}
        />
      )}
    </div>
  );
};

export default ResourceScheduleModal;
